import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { CompositionBar } from '../components/charts.tsx';
import { Icon } from '../components/Icon.tsx';
import { CountUp, EASE, Reveal } from '../components/motion.tsx';
import { Avatar, Button, Callout, Chip, Dialog, Field, PageHead, Panel, Skeleton, StatusChip, useConfirm, useToast } from '../components/ui.tsx';
import { ApiError, get, photoUrl, post } from '../lib/api.ts';
import { addDays, date, dateRange, idr, idrCompact, relative, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { GenerateGoodsButton } from './Goods.tsx';

interface RunDetail {
  id: string;
  office: string;
  periodFrom: string;
  periodTo: string;
  transferDate: string | null;
  status: string;
  headcount: number;
  totalNet: string;
  totalTax: string;
  engineVersion: string | null;
  parityFlags: Record<string, boolean> | null;
  taxMethod: string;
  issues: { employeeId: string; problem: string; detail: string }[] | null;
  calculatedAt: string | null;
  calculatedBy: string | null;
  approvedAt: string | null;
  cancelReason: string | null;
  byComponent: { name: string; kind: 'earning' | 'deduction'; total: string }[];
  payslips: { id: string; employeeId: string; name: string; employeeNo: string | null; basicSalary: string; totalEarnings: string; totalDeductions: string; netPay: string; warnings: string[]; isSecondCompany: boolean; tax: string | null }[];
}
interface Transfers {
  count: number;
  issues: { employeeId: string; employeeNo: string; fullName: string; problem: string; detail: string }[];
  byBank: [string, number][];
}

const STAGES = ['draft', 'review', 'approved', 'paid'] as const;
const TONES = ['var(--cat-1)', 'var(--cat-2)', 'var(--cat-3)', 'var(--cat-4)', 'var(--cat-5)', 'var(--cat-6)'];

const FLAG_TEXT: Record<string, string> = {
  swapPrevCurrentReceived: 'P1 · prev/current salary swap in prorate formulas',
  bracketWidthIsAbsoluteMax: 'P17 · PPh 21 bracket width uses absolute max',
  topBracketUsesMin: 'P2 · top bracket multiplies by its minimum',
  unsetVariableIsZeroFormula: 'Unset formula variable → whole formula 0',
  divisionByZeroIsZero: 'Division by zero → formula 0',
  thrAccrualSumsGeneralDeductions: 'THR accrual sums general deductions',
  thrIgnoresInactiveFlag: 'P15 · THR ignores inactive rules',
  thrZeroFallsBackToAmount: 'P16 · THR zero falls back to static amount',
  thrZeroMonthsIsFullYear: 'P12 · 0 months worked = full THR',
};

export function PayrollRun() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const run = useQuery({ queryKey: ['run', id], queryFn: () => get<RunDetail>(`/api/payroll/runs/${id}`) });
  const transfers = useQuery({ queryKey: ['run-transfers', id], queryFn: () => get<Transfers>(`/api/payroll/runs/${id}/transfers`), enabled: me.access.payrollRun || me.access.payrollApprove });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [sort, setSort] = useState<'name' | 'net'>('name');
  const r = run.data;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['run', id] });
    void qc.invalidateQueries({ queryKey: ['runs'] });
    void qc.invalidateQueries({ queryKey: ['run-transfers', id] });
  };
  const errToast = (e: unknown) => toast({ title: 'That didn’t work', body: e instanceof ApiError ? e.message : undefined, tone: 'error' });

  const calc = useMutation({ mutationFn: () => post(`/api/payroll/runs/${id}/calculate`), onSuccess: () => (toast({ title: 'Recalculated' }), refresh()), onError: errToast });
  const approve = useMutation({
    mutationFn: () => post<{ expired: { leave: number; overtime: number; corrections: number } }>(`/api/payroll/runs/${id}/approve`),
    onSuccess: (res) => {
      const n = res.expired.leave + res.expired.overtime + res.expired.corrections;
      toast({ title: 'Run approved', body: `Attendance for this period is now locked.${n ? ` ${n} request${n === 1 ? '' : 's'} still waiting in it expired.` : ''}` });
      refresh();
      if (n) for (const k of ['inbox', 'inbox-count']) void qc.invalidateQueries({ queryKey: [k] });
    },
    onError: errToast,
  });
  const cancel = useMutation({ mutationFn: () => post(`/api/payroll/runs/${id}/cancel`, { reason }), onSuccess: () => (setCancelOpen(false), toast({ title: 'Run cancelled' }), refresh()), onError: errToast });
  const exportM = useMutation({
    mutationFn: () => post<{ fileId: string; filename: string }>(`/api/payroll/runs/${id}/export`),
    onSuccess: (f) => {
      window.location.assign(`/api/payroll/files/${f.fileId}`);
      toast({ title: 'Bank files ready', body: f.filename });
    },
    onError: errToast,
  });

  const earnings = useMemo(() => (r?.byComponent ?? []).filter((c) => c.kind === 'earning').slice(0, 6), [r]);
  const deductions = useMemo(() => (r?.byComponent ?? []).filter((c) => c.kind === 'deduction').slice(0, 6), [r]);
  const slips = useMemo(() => [...(r?.payslips ?? [])].sort((a, b) => (sort === 'net' ? Number(b.netPay) - Number(a.netPay) : a.name.localeCompare(b.name))), [r, sort]);
  const stageIdx = r ? STAGES.indexOf(r.status as (typeof STAGES)[number]) : -1;
  const net = idrCompact(r?.totalNet);
  const legacyOn = r?.parityFlags ? Object.entries(r.parityFlags).filter(([, v]) => v) : [];

  if (run.isLoading) return <Skeleton h={480} />;
  if (!r) return <Callout tone="bad">This run could not be loaded.</Callout>;
  // The server refuses approval until the period has ended: days still ahead count as absences.
  const periodOpen = (r.status === 'draft' || r.status === 'review') && r.periodTo >= todayIn();

  return (
    <div>
      {confirm.node}
      <PageHead
        compact
        eyebrow={`Payroll · ${r.office}`}
        title={
          <>
            {dateRange(r.periodFrom, r.periodTo)}
            {r.status === 'cancelled' ? <em> (cancelled)</em> : null}
          </>
        }
        lede={r.transferDate ? `Transfer on ${date(r.transferDate, { weekday: true })}. Calculated ${r.calculatedAt ? relative(r.calculatedAt) : '—'} with ${r.engineVersion ?? 'engine'}, PPh 21 ${r.taxMethod}.` : undefined}
        aside={
          <>
            {r.headcount ? (
              <Button icon="download" onClick={() => window.location.assign(`/api/payroll/runs/${r.id}/payslips.xlsx`)}>
                Excel
              </Button>
            ) : null}
            {(r.status === 'draft' || r.status === 'review') && me.access.payrollRun ? (
              <Button icon="refresh" loading={calc.isPending} onClick={() => calc.mutate()}>
                Recalculate
              </Button>
            ) : null}
            {r.status === 'review' && me.access.payrollApprove ? (
              <Button
                variant="primary"
                icon="check"
                loading={approve.isPending}
                disabled={r.calculatedBy === me.userId || periodOpen}
                title={r.calculatedBy === me.userId ? 'You calculated this run, so someone else must approve it.' : periodOpen ? `The period runs until ${date(r.periodTo)}.` : undefined}
                onClick={async () => {
                  if (await confirm.ask('Approve this run?', `${r.headcount} payslips totalling ${idr(r.totalNet)}. Attendance for the period will be locked and payslips released to employees.`, 'Approve run')) approve.mutate();
                }}
              >
                Approve
              </Button>
            ) : null}
            {(r.status === 'approved' || r.status === 'paid') && me.access.payrollApprove ? (
              <Button variant="primary" icon="download" loading={exportM.isPending} disabled={!!transfers.data?.issues.length} onClick={() => exportM.mutate()}>
                Bank files
              </Button>
            ) : null}
            {(r.status === 'approved' || r.status === 'paid') && me.access.goodsPurchase && r.payslips.some((p) => p.isSecondCompany) ? <GenerateGoodsButton kind="payroll" runId={r.id} /> : null}
            {r.status !== 'cancelled' && r.status !== 'paid' && me.access.payrollApprove ? (
              <Button variant="ghost" onClick={() => setCancelOpen(true)}>
                Cancel run
              </Button>
            ) : null}
          </>
        }
      />

      {periodOpen ? (
        <div style={{ marginBottom: 'var(--s-5)' }}>
          <Callout tone="warn" title={`The period runs until ${date(r.periodTo)}`}>
            Days from today on count as absences in this calculation. Recalculate after the period ends; approval opens on {date(addDays(r.periodTo, 1))}.
          </Callout>
        </div>
      ) : null}

      <Reveal>
        <Reveal.Item>
          <ol className="row run-steps" style={{ listStyle: 'none', padding: 0, margin: '0 0 var(--s-6)', ['--gap' as string]: '0' }} aria-label="Run progress">
            {STAGES.map((st, i) => (
              <li key={st} className="row" style={{ flex: 1, ['--gap' as string]: '10px' }}>
                <span style={{ width: 22, height: 22, borderRadius: 11, display: 'grid', placeItems: 'center', border: '1px solid', borderColor: i <= stageIdx ? 'var(--accent)' : 'var(--line-3)', background: i < stageIdx ? 'var(--accent)' : 'transparent', color: i < stageIdx ? 'var(--accent-ink)' : i === stageIdx ? 'var(--accent-text)' : 'var(--ink-4)' }}>
                  {i < stageIdx ? <Icon name="check" width={13} height={13} /> : <span className="mono" style={{ fontSize: 10 }}>{i + 1}</span>}
                </span>
                <span className="eyebrow" style={{ color: i === stageIdx ? 'var(--ink-1)' : undefined }}>
                  {st === 'review' ? 'In review' : st}
                </span>
                {i < STAGES.length - 1 ? <span className="run-steps__line" style={{ flex: 1, height: 1, background: i < stageIdx ? 'var(--accent-line)' : 'var(--line-2)', margin: '0 12px' }} /> : null}
              </li>
            ))}
          </ol>
        </Reveal.Item>

        <Reveal.Item>
          <div className="ledger" style={{ ['--cols' as string]: 4 }}>
            <div className="ledger__cell ledger__cell--accent">
              <div className="ledger__value">
                {net.value}
                <small>{net.unit}</small>
              </div>
              <div className="ledger__label eyebrow">Total net pay</div>
              <div className="ledger__delta num">{idr(r.totalNet)}</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value">
                <CountUp value={r.headcount} />
              </div>
              <div className="ledger__label eyebrow">Payslips</div>
              <div className="ledger__delta">{r.payslips.filter((p) => p.isSecondCompany).length} second company</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value">
                {idrCompact(r.totalTax).value}
                <small>{idrCompact(r.totalTax).unit}</small>
              </div>
              <div className="ledger__label eyebrow">PPh 21</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value" style={{ color: transfers.data?.issues.length ? 'var(--rose)' : 'var(--sage)' }}>{transfers.data ? transfers.data.issues.length : '—'}</div>
              <div className="ledger__label eyebrow">Transfer blockers</div>
              <div className="ledger__delta">{transfers.data?.byBank.map(([b, n]) => `${b} ${n}`).join(' · ')}</div>
            </div>
          </div>
        </Reveal.Item>

        {r.status === 'cancelled' && r.cancelReason ? (
          <Reveal.Item style={{ marginTop: 20 }}>
            <Callout tone="bad" title="Cancelled.">
              {r.cancelReason}
            </Callout>
          </Reveal.Item>
        ) : null}

        <Reveal.Item className="grid-12" style={{ marginTop: 'var(--s-6)' }}>
          <div className="col-7 stack">
            <Panel eyebrow="Where the money goes" title="Earnings">
              <CompositionBar parts={earnings.map((c, i) => ({ label: c.name, value: Number(c.total), tone: TONES[i % TONES.length]! }))} />
            </Panel>
            <Panel eyebrow="What’s withheld" title="Deductions">
              <CompositionBar parts={deductions.map((c, i) => ({ label: c.name, value: Number(c.total), tone: TONES[(i + 3) % TONES.length]! }))} />
            </Panel>
          </div>
          <div className="col-5 stack">
            {transfers.data?.issues.length ? (
              <Panel eyebrow="Before bank files" title={<>Fix these <em className="serif-italic">first</em></>}>
                <p className="muted" style={{ fontSize: 'var(--step--1)', marginBottom: 12 }}>
                  The old system silently paid these people into a default account. Hearth refuses until each one is fixed.
                </p>
                <ul className="list-plain">
                  {transfers.data.issues.map((i) => (
                    <li key={i.employeeId} className="mini-row" style={{ alignItems: 'start' }}>
                      <Avatar name={i.fullName} size={28} photo={photoUrl(i.employeeId)} />
                      <div>
                        <Link to="/people/$id" params={{ id: i.employeeId }} className="link">
                          {i.fullName}
                        </Link>
                        <div className="faint" style={{ fontSize: 'var(--step--2)' }}>
                          {i.detail}
                        </div>
                      </div>
                      <span style={{ marginLeft: 'auto' }}>
                        <Chip tone="bad">{i.problem.replace('_', ' ')}</Chip>
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            ) : transfers.data ? (
              <Callout tone="info" title="Every transfer is valid.">
                {transfers.data.count} accounts checked for bank and number format.
              </Callout>
            ) : null}
            <Panel eyebrow="Calculation mode" title="Parity">
              {legacyOn.length ? (
                <>
                  <p className="muted" style={{ fontSize: 'var(--step--1)', marginBottom: 10 }}>
                    This run reproduces the legacy engine exactly, including these known behaviours (see the audit). Switch to corrected rules in Settings once HR and tax sign off.
                  </p>
                  <ul className="list-plain">
                    {legacyOn.map(([k]) => (
                      <li key={k} className="mini-row">
                        <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--ochre)' }} />
                        {FLAG_TEXT[k] ?? k}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="muted">Corrected rules — all legacy defects fixed.</p>
              )}
            </Panel>
          </div>
        </Reveal.Item>

        <Reveal.Item style={{ marginTop: 'var(--s-6)' }}>
          <Panel
            eyebrow={`${r.payslips.length} payslips`}
            title="People"
            action={
              <select className="select" style={{ width: 'auto', height: 32 }} value={sort} onChange={(e) => setSort(e.target.value as 'name' | 'net')} aria-label="Sort">
                <option value="name">By name</option>
                <option value="net">By net pay</option>
              </select>
            }
          >
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="right">Basic</th>
                    <th className="right">Earnings</th>
                    <th className="right">Deductions</th>
                    <th className="right">PPh 21</th>
                    <th className="right">Net</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {slips.map((p, i) => (
                    <motion.tr key={p.id} data-href onClick={() => navigate({ to: '/payslips/$id', params: { id: p.id } })} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 30) * 0.012, duration: 0.4, ease: EASE }}>
                      <td>
                        <div className="row">
                          <Avatar name={p.name} size={26} />
                          <span>{p.name}</span>
                          {p.isSecondCompany ? <Chip tone="info">2nd co.</Chip> : null}
                          {p.warnings.length ? (
                            <span title={p.warnings.join('\n')} style={{ color: 'var(--ochre)' }}>
                              <Icon name="alert" width={14} height={14} />
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="right num muted">{idr(p.basicSalary)}</td>
                      <td className="right num">{idr(p.totalEarnings)}</td>
                      <td className="right num muted">{idr(p.totalDeductions)}</td>
                      <td className="right num muted">{idr(p.tax)}</td>
                      <td className="right num" style={{ fontWeight: 600 }}>
                        {idr(p.netPay)}
                      </td>
                      <td className="right">
                        <Icon name="chevronRight" width={16} height={16} className="faint" />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </Reveal.Item>
      </Reveal>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        eyebrow="Cancel run"
        title={<>Why cancel?</>}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              Keep it
            </Button>
            <Button variant="danger" loading={cancel.isPending} disabled={reason.trim().length < 5} onClick={() => cancel.mutate()}>
              Cancel run
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Unlocks attendance and returns one-off adjustments to the queue.">
          {(p) => <textarea {...p} className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
      </Dialog>
    </div>
  );
}
